import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaSync, FaLock } from "react-icons/fa";
import Sidebar from "../Sidebar/Sidebar";
import "./ManageInventory.css";
import useAuthCheck from "../../useAuthCheck";
import api from "../../api";
import { jwtDecode } from "jwt-decode";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";

const getNetworkErrorMessage = (err) => {
  const isNetworkError = (
    !navigator.onLine ||
    err.code === "ERR_NETWORK" ||
    err.code === "ERR_INTERNET_DISCONNECTED" ||
    err.code === "ERR_NAME_NOT_RESOLVED" ||
    err.code === "ERR_CONNECTION_REFUSED" ||
    err.code === "ERR_CONNECTION_TIMED_OUT" ||
    err.message === "Network Error" ||
    err.message?.includes("ERR_INTERNET_DISCONNECTED") ||
    err.message?.includes("net::")
  );
  if (isNetworkError) {
    if (IS_DESKTOP) return "🔧 Cannot connect to local server. Please restart the MyApoti desktop app.";
    return "📡 No internet connection. Please check your network and try again.";
  }
  return null;
};

const ManageInventory = () => {
  useAuthCheck();

  const [isLoading, setIsLoading] = useState(true);
  const [userType, setUserType] = useState(null);
  const [hasInventoryPermission, setHasInventoryPermission] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [staffPermissions, setStaffPermissions] = useState([]);
  const [networkError, setNetworkError] = useState("");

  // ── RetailMan connector state (desktop only) ──
  const [retailmanEnabled, setRetailmanEnabled] = useState(false);
  const [retailmanFolder, setRetailmanFolder] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setAccessDenied(true);
          return;
        }

        const decoded = jwtDecode(token);
        const type = (decoded.user_type?.value || decoded.user_type || "")
          .toString()
          .trim();

        setUserType(type);

        if (type === "PHARMACY") {
          setHasInventoryPermission(true);
        } else if (type === "PHARMACYBRANCH") {
          setHasInventoryPermission(true);
        } else if (type === "PHARMACYSTAFF") {
          try {
            const response = await api.get("/staffmanager/my-permissions");
            const permissions = response.data || [];
            setStaffPermissions(permissions);

            const hasInventory = permissions.some((perm) => {
              const upperPerm = perm.toUpperCase();
              return (
                upperPerm === "INVENTORY_MANAGE" ||
                upperPerm === "INVENTORY_MANAGEMENT" ||
                upperPerm.includes("INVENTORY")
              );
            });

            if (hasInventory) {
              setHasInventoryPermission(true);
            } else {
              setAccessDenied(true);
            }
          } catch (error) {
            const netMsg = getNetworkErrorMessage(error);
            if (netMsg) {
              setNetworkError(netMsg);
            } else {
              setAccessDenied(true);
            }
          }
        } else {
          setAccessDenied(true);
        }

        // ── Fetch RetailMan connector settings (desktop only) ──
        if (IS_DESKTOP) {
          try {
            const res = await api.get("/connectors/settings");
            setRetailmanEnabled(res.data?.retailman?.enabled || false);
            setRetailmanFolder(res.data?.retailman?.folder || null);
          } catch {
            // non-fatal — defaults to false
          }
        }

      } catch (error) {
        const netMsg = getNetworkErrorMessage(error);
        if (netMsg) {
          setNetworkError(netMsg);
        } else {
          setAccessDenied(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  const renderAccessDenied = () => (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-dashboard">
        <div className="card">
          <div className="access-denied-box fade-in">
            <h2>❌ Access Denied</h2>
            <p>You do not have permission to access Inventory Management.</p>
            <p className="access-denied-subtitle">
              {userType === "PHARMACYSTAFF"
                ? `Your staff account does not have the INVENTORY_MANAGEMENT permission.\n\nAvailable permissions: ${staffPermissions.length > 0 ? staffPermissions.join(", ") : "None assigned"}`
                : "Only pharmacy owners, branch managers, and authorized staff can manage inventory."}
            </p>
            <Link to="/" className="back-btn">← Go Back to Dashboard</Link>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <div className="main-dashboard">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading inventory management...</p>
          </div>
        </div>
      </div>
    );
  }

  if (networkError) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <div className="main-dashboard">
          <div className="card">
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <p style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>{IS_DESKTOP ? "🔧" : "📡"}</p>
              <h3 style={{ color: "#dc3545", marginBottom: "0.5rem" }}>Connection Error</h3>
              <p style={{ color: "#555", marginBottom: "1.5rem" }}>{networkError}</p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "0.6rem 1.5rem", background: "#dc3545", color: "white",
                  border: "none", borderRadius: "6px", cursor: "pointer",
                  fontWeight: "600", fontSize: "0.95rem",
                }}
              >
                🔄 Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied || !hasInventoryPermission) {
    return renderAccessDenied();
  }

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-dashboard">
        <div className="card">
          <h1 className="title">📦 Inventory Management</h1>

          {/* ── RetailMan active banner ── */}
          {IS_DESKTOP && retailmanEnabled && (
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              background: "#fff8e1",
              border: "1px solid #ffc107",
              borderLeft: "4px solid #ffc107",
              borderRadius: "8px",
              padding: "14px 16px",
              marginBottom: "20px",
            }}>
              <span style={{ fontSize: "1.4rem", lineHeight: 1 }}>🔗</span>
              <div>
                <div style={{ fontWeight: 700, color: "#856404", marginBottom: "4px" }}>
                  RetailMan Sync is Active
                </div>
                <div style={{ fontSize: "0.88rem", color: "#856404", lineHeight: 1.5 }}>
                  Your inventory is managed by RetailMan. Adding, editing and deleting
                  stock must be done in RetailMan — changes sync to MyApoti automatically
                  every 10 minutes.
                  {retailmanFolder && (
                    <span style={{
                      display: "block",
                      marginTop: "4px",
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      color: "#a07000",
                    }}>
                      📁 {retailmanFolder}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="button-grid">

            {/* ── Add Inventory Manually ── */}
            {retailmanEnabled ? (
              <div
                className="btn btn-disabled"
                title="Disabled — manage stock in RetailMan"
                style={{ opacity: 0.5, cursor: "not-allowed", position: "relative" }}
              >
                <FaLock style={{ marginRight: "6px", fontSize: "0.85rem" }} />
                Add Inventory Manually
              </div>
            ) : (
              <Link to="/AddInventoryManually" className="btn">
                ➕ Add Inventory Manually
              </Link>
            )}

            {/* ── Upload CSV ── */}
            {retailmanEnabled ? (
              <div
                className="btn btn-disabled"
                title="Disabled — manage stock in RetailMan"
                style={{ opacity: 0.5, cursor: "not-allowed" }}
              >
                <FaLock style={{ marginRight: "6px", fontSize: "0.85rem" }} />
                Upload CSV Inventory
              </div>
            ) : (
              <Link to="/UploadInventoryCSV" className="btn">
                📤 Upload CSV Inventory
              </Link>
            )}

            {/* ── Update CSV ── */}
            {retailmanEnabled ? (
              <div
                className="btn btn-disabled"
                title="Disabled — manage stock in RetailMan"
                style={{ opacity: 0.5, cursor: "not-allowed" }}
              >
                <FaLock style={{ marginRight: "6px", fontSize: "0.85rem" }} />
                Update CSV Inventory
              </div>
            ) : (
              <Link to="/UpdateInventoryCSV" className="btn">
                <FaSync style={{ marginRight: "6px" }} />
                Update CSV Inventory
              </Link>
            )}

            {/* ── List Inventory — always available ── */}
            <Link to="/InventoryItem" className="btn">
              📋 List Inventory
            </Link>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageInventory;